import React, { useRef, useCallback, useContext } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import LayoutContainer from 'components/Common/LayoutContainer'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Image from 'components/Common/Image'
import HeadData from 'components/Common/HeadData'
import ImageHero from 'components/Common/ImageHero'
import Faq from 'components/Common/Faq'
import { mediaQueryUp } from 'components/utils/breakpoint'
import InsuranceIcon from 'LuxKit/Icons/custom/InsuranceIcon'
import VacationIcon from 'LuxKit/Icons/custom/VacationIcon'
import { InsuranceProducts } from './CoverGeniusData'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import { connect } from 'react-redux'
import useDelayedJumpToHash from 'hooks/useDelayedJumpToHash'
import { addGTMEvent } from 'api/googleTagManager'
import {
  insuranceLandingPageViewCoverOptionsClick,
  insuranceLandingPageGetQuoteClick,
} from 'analytics/eventDefinitions'
import { capitalise } from 'lib/string/stringUtils'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import Group from 'components/utils/Group'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import PageBanner from 'LuxKit/Banners/PageBanner'
import LuxPlusLogoWrapper from 'luxPlus/components/LuxPlusLogoWrapper'
import { LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import { getThemeClassName } from 'lib/theme/themeUtils'
import { getIsInsuranceAndRefundProtectionLuxPlusBenefitsEnabled, getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FormatCurrency from 'components/Common/FormatCurrency'
import ContactContext from 'contexts/contactContext'

const HeroImage = styled(ImageHero)`
  max-height: ${rem(470)};
`

const Section = styled.div`
  background-color: ${props => props.theme.palette.neutral.default.eight};

  &:nth-child(odd) {
    background-color: ${props => props.theme.palette.neutral.default.six};
  }
`

const InnerSection = styled(LayoutContainer).attrs<any>({
  size: 'md',
})`
  padding: ${rem(32)} ${rem(40)};

  ${mediaQueryUp.desktop} {
    padding: ${rem(60)} ${rem(32)};
  }
`

const FaqInnerSection = styled(InnerSection)`
  padding-top: ${rem(16)};
`

const SectionBlurb = styled(BodyText)`
  margin-top: ${rem(16)};
`

const SubSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: ${rem(24)};

  ${mediaQueryUp.desktop} {
    margin-top: ${rem(40)};
  }

  > * + * {
    margin-top: ${rem(16)};
  }
`

const SectionContent = styled.div`
  margin-top: ${rem(32)};
  width: 100%;
`

const InsuranceProductBody = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${rem(20)} ${rem(24)};
  max-width: ${rem(320)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  justify-content: space-between;
`

const InsuranceProductButtons = styled.div`
  margin-top: ${rem(18)};
`

const ProductTitle = styled(BodyText)`
  margin-top: ${rem(5)};
`

const ProductBlurb = styled(BodyText)`
  margin-top: ${rem(12)};
`

const CenteredGallery = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;

  ${mediaQueryUp.desktop} {
    flex-direction: row;
    justify-content: center;
    align-items: stretch;
  }
`

const ProductsGallery = styled(CenteredGallery)`
  > * + * {
    margin-top: ${rem(16)};
  }

  ${mediaQueryUp.desktop} {
    > * + * {
      margin-left: ${rem(16)};
      margin-top: 0;
    }
  }
`

const WhyBuyGallery = styled(CenteredGallery)`
  > * {
    max-width: ${rem(424)};
  }

  > * + * {
    margin-top: ${rem(32)};
  }

  ${mediaQueryUp.desktop} {
    > * + * {
      margin-left: ${rem(32)};
      margin-top: 0;
    }
  }
`

const WhyBuyContainer = styled.div`
  text-align: center;
  border: solid 1px ${props => props.theme.palette.neutral.default.five};
  padding: ${rem(44)} ${rem(44)} ${rem(32)};

  ${mediaQueryUp.desktop} {
    max-width: ${rem(424)};
  }

  >:nth-child(2) {
    margin-top: ${rem(24)};
  }

  >:nth-child(3) {
    margin-top: ${rem(8)};
  }
`

const WhyByVisual = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height:  ${rem(50)};
`

const TickItems = styled.div`
  text-align: left;
  margin-top: ${rem(16)};

  > * + * {
    margin-top: ${rem(8)};
  }
`

const HeroSectionContent = styled(InnerSection)`
  align-items: center;
  justify-content: center;
  position: relative;
`

const HeroBlurb = styled(Heading)`
  margin-top: ${rem(12)};
`

const HeroButton = styled(TextButton)`
  margin-left: auto;
  margin-right: auto;
  margin-top: ${rem(16)};
  display: block;
`

const CallUsBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  > * + * {
    margin-top: ${rem(8)};
  }

  ${mediaQueryUp.desktop} {
    flex-direction: row;

    > * + * {
      margin-left: ${rem(8)};
      margin-top: 0;
    }
  }
`

const PlusText = styled(Heading)`
  color: ${props => props.theme.palette.brand.primary.normal};
`

interface MappedProps {
  insuranceProductName: string;
  regionCode: string;
  luxPlusEnabled: boolean;
  insuranceAndRefundProtectionEnabled: boolean;
}

interface Props extends MappedProps {}

function TravelInsurancePage(props: Props) {
  const { insuranceProductName, regionCode, luxPlusEnabled, insuranceAndRefundProtectionEnabled } = props

  const contact = useContext(ContactContext)
  const comparePlansRef = useRef<HTMLDivElement>(null)

  const onViewOptionsClick = useCallback(() => {
    comparePlansRef.current?.scrollIntoView()

    addGTMEvent(insuranceLandingPageViewCoverOptionsClick())
  }, [])

  const onGetQuote = useCallback(() => {
    addGTMEvent(insuranceLandingPageGetQuoteClick())
  }, [])

  useDelayedJumpToHash()

  return (
    <>
      {luxPlusEnabled && insuranceAndRefundProtectionEnabled && <PageBanner
        kind="lux-plus"
        className={getThemeClassName({ mobile: 'inverse' })}
        align="center"
        description={<Group direction="vertical" desktopDirection="horizontal" horizontalAlign="center" gap={12} desktopGap={20}>
          <LuxPlusLogoWrapper height={18} />
          <Group direction="vertical" desktopDirection="horizontal" horizontalAlign="center" gap={4} desktopGap={20}>
            Members save 40% on Travel & Cancellation Protection^ <TextLink weight="bold" to={`/${LUXURY_PLUS_PATHS.LANDING_PAGE}`}>View all benefits</TextLink>
          </Group>
        </Group>}
      />}
      <HeadData title={`Travel ${capitalise(insuranceProductName)}`} />
      <HeroImage
        alt={`Travel ${capitalise(insuranceProductName)} Banner`}
        aria-hidden="true"
        brightness={0}
        gravity="center"
        mobileAspectRatio="8:9"
        tabletAspectRatio="2:1"
        desktopAspectRatio="8:3"
        largeDesktopAspectRatio="3:1"
        id="23sfdwahze86vktpobuh"
      >
        <HeroSectionContent>
          <Heading variant="heading2" align="center" colour="neutral-eight" format="titlecase">
            Luxury Escapes Travel&nbsp;{insuranceProductName}
          </Heading>
          <HeroBlurb variant="heading3" align="center" colour="neutral-eight" forwardedAs="h2">
            Prepare for the unexpected
          </HeroBlurb>
          <HeroButton kind="primary" onClick={onViewOptionsClick}>
            View cover options
          </HeroButton>
        </HeroSectionContent>
      </HeroImage>
      <Section>
        <InnerSection>
          <LayoutContainer size="sm">
            <Heading variant="heading3">
              Want {insuranceProductName} from the unexpected on your next Luxury Escape?
            </Heading>
          </LayoutContainer>
          <SectionBlurb variant="large" align="center">
            Accidents can happen to anyone and can leave you out of pocket. Travel {insuranceProductName} may offer {insuranceProductName} when things go wrong before or during your travels. Benefits may include: travel delays or cancellations, medical and hospital cover, and protection for lost, stolen or damaged belongings.  Can you afford the risk of exploring the world without travel {insuranceProductName}?
          </SectionBlurb>
        </InnerSection>
      </Section>
      <Section ref={comparePlansRef}>
        <InnerSection>
          <Heading variant="heading3" align="center" format="titlecase">Compare our Travel&nbsp;{insuranceProductName}&nbsp;Plans</Heading>
          <SectionBlurb variant="large" align="center">We have three plans with different levels of cover.</SectionBlurb>

          <SectionContent>
            <ProductsGallery>
              {InsuranceProducts.map(product => (
                <InsuranceProductBody key={product.title}>
                  <div>
                    <BodyText variant="medium" format="uppercase" align="center">{product.type}</BodyText>
                    <ProductTitle variant="medium" weight="bold" align="center" forwardedAs="h3">{product.title}</ProductTitle>
                    {product.blurb && <ProductBlurb variant="medium">{product.blurb}</ProductBlurb>}
                    <TickItems>
                      {product.benefits.map((benefit, index) => <BodyTextBlock
                        key={index}
                        variant="medium"
                        startIcon={<LineCheckIcon colour="success" />}
                      >
                        {benefit.text}
                      </BodyTextBlock>)}
                      {product.complementaryText && <BodyText variant="medium">{product.complementaryText}</BodyText>}
                    </TickItems>
                  </div>
                  <InsuranceProductButtons>
                    <TextButton
                      kind="primary"
                      data-testid="get-a-quote"
                      fit="full-width"
                      onClick={onGetQuote}
                      to="/travel-protection/quote"
                    >
                      Get a Quote
                    </TextButton>
                  </InsuranceProductButtons>
                </InsuranceProductBody>
              ))}
            </ProductsGallery>
          </SectionContent>
          <SubSection>
            <BodyText variant="large">Need more information about Luxury Escapes Travel {capitalise(insuranceProductName)}? </BodyText>
            <CallUsBlock>
              <BodyText variant="large" weight="bold"> Call us on </BodyText>
              <TextLink startIcon={<LinePhoneIcon />} href={`tel:${contact.defaultContact.number}`}>
                {contact.defaultContact.humanReadable}
              </TextLink>
            </CallUsBlock>
          </SubSection>
        </InnerSection>
      </Section>
      <Section>
        <InnerSection>
          <Heading variant="heading3" align="center">Why buy travel { insuranceProductName } with us?</Heading>
          <SectionBlurb variant="large" align="center">Protecting the world's greatest holidays with reputable travel {insuranceProductName}</SectionBlurb>
          <SectionContent>
            <WhyBuyGallery>
              <WhyBuyContainer>
                <WhyByVisual>
                  <Group direction="horizontal" verticalAlign="center" horizontalAlign="center" gap={8}>
                    <VacationIcon colour="primary" size={48} />
                    <PlusText variant="heading3">+</PlusText>
                    <InsuranceIcon colour="primary" size={48} />
                  </Group>
                </WhyByVisual>
                <Heading variant="heading6" format="titlecase" as="h3">
                  Bundle Your Holiday + Travel {insuranceProductName}
                </Heading>
                <BodyText variant="large">
                  Easily add travel { insuranceProductName } to your upcoming Luxury Escape.
                </BodyText>
              </WhyBuyContainer>
              <WhyBuyContainer>
                <WhyByVisual>
                  <Image
                    id="34vqalq5dzj565v16rzb"
                    alt="Xcover"
                  />
                </WhyByVisual>
                <Heading variant="heading6" as="h3">We’ve partnered with XCover</Heading>
                <BodyText variant="large">
                  XCover makes travel {insuranceProductName} simple, fast, and stress-free — which is why over 3 million customers trust us.
                </BodyText>
              </WhyBuyContainer>
            </WhyBuyGallery>
          </SectionContent>
        </InnerSection>
        <FaqInnerSection>
          <Heading variant="heading3" align="center" id="faqs">Frequently Asked Questions</Heading>
          <SectionContent>
            <Faq slug="xcover-faqs" />
          </SectionContent>
        </FaqInnerSection>
        <InnerSection>
          <LayoutContainer size="sm">
            <Heading variant="heading3" align="center">Disclaimer</Heading>
          </LayoutContainer>
          <VerticalSpacer gap={40}>
            <SectionBlurb variant="large" align="center">
              {regionCode === 'AU' && (
                <>
                  You should consider the {' '}
                  <TextLink weight="regular" target="_blank" href="https://www.xcover.com/en/pds/luxury-escapes-au-pds">
                    PDS/FSG/TMD
                  </TextLink>
                  {' '}and confirm that you are eligible for coverage, including the age limit. Pacific International Insurance Pty Limited ABN 83 169 311 193 is the underwriter and XCover, a trading name of Cover Genius Pty Limited ABN 43 159 983 598, AFSL 490058, is the issuer. Luxury Escapes Travel Pty Ltd ABN 16 150 650 927 is the authorised representative (AR No 001288350).
                </>
              )}
              {regionCode === 'NZ' && (
                <>
                  You should consider the {' '}
                  <TextLink weight="regular" target="_blank" href="https://www.xcover.com/en/pds/luxury-escapes-au-pds">
                    PDS
                  </TextLink>
                  {' '} and confirm that you are eligible for coverage, including the age limit. Pacific International Insurance Pty Limited ABN 83 169 311 193 is the underwriter and XCover, a trading name of Cover Genius Pty Limited ABN 43 159 983 598, is the issuer.
                </>
              )}
              {regionCode !== 'AU' && regionCode !== 'NZ' && (
                <>
                  You should consider the Plan Terms and confirm that you are eligible for coverage, including the age limit. The Plan is provided by Cover Genius Singapore Pte. Ltd. The Medical cover is underwritten by Isosceles PCC Limited - Cell Hotspot.
                </>
              )}
            </SectionBlurb>
            {luxPlusEnabled && insuranceAndRefundProtectionEnabled && <BodyText variant="small">
              ^Members receive 40% off Travel and Cancellation Protection product(s) (Product) up to a maximum discount of <FormatCurrency value={300} format="rounded" />. The Product may not be available to all members and eligibility and coverage restrictions apply.{' '}
              Members must read and consider the relevant PDS to ensure whether and how the Product applies to them. The relevant terms and PDS of the Product will apply to any purchase.{' '}
              The Product benefit is not exchangeable for cash or credit if not able to be utilised for any reason. <TextLink weight="regular" to="/terms-and-conditions">T&Cs apply.</TextLink>
            </BodyText>}
          </VerticalSpacer>
        </InnerSection>
      </Section>
    </>
  )
}

function mapStateToProps(state: App.State): MappedProps {
  return ({
    insuranceProductName: state.geo.insuranceProductName,
    regionCode: state.geo.currentRegionCode,
    luxPlusEnabled: getIsLuxPlusEnabled(state),
    insuranceAndRefundProtectionEnabled: getIsInsuranceAndRefundProtectionLuxPlusBenefitsEnabled(state),
  })
}

export default connect(mapStateToProps)(TravelInsurancePage)
